import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { Router } from '@angular/router';
import * as XLSX from 'xlsx';
import { StatsService } from '../stats.service';
import { LoginService } from '../login.service';
import { ExamsService } from '../exams.service';
import { PatientsService } from '../patients.service';

interface DatamineHistoryEntry {
  query: string;
  explanation: string;
  notes: string;
  results: any[][];
  exportResults: any[][];
  userRequest: string;
  userResponse: string | null;
  tempUserResponse: string;
  status: string;
  expanded: boolean;
  dataOutput: string;
  previewLimit: number;
  totalRowCount: number;
  displayColumns: string[];
  hiddenColumns: string[];
  downloadFileName: string;
}


@Component({
  selector: 'app-nl-datamine',
  templateUrl: './nl-datamine.component.html',
  styleUrls: ['./nl-datamine.component.scss'],
  standalone: false,
})
export class NlDatamineComponent implements OnInit {
  userRequest: string = '';
  AIresponse: any = null;
  AIError: string = '';
  AImodel: string = 'claude-opus-5'; // Default AI model
  showButton: boolean = true; // Control visibility of the button
  runGlobally: boolean = false; // Whether to run search across all clinics

  aiHistory: DatamineHistoryEntry[] = [];
  showLog: boolean = false;
  logEntries: { timestamp: Date; question: string; isFollowup: boolean; model: string; rawResponse: any }[] = [];
  readonly defaultPreviewLimits: {[key: string]: number} = {
    listing_of_exams: 200,
    listing_of_patients: 200,
    free_form: 400,
  };
  readonly columnLabels: {[key: string]: string} = {
    PatientID: 'ID',
    PatientName: 'Name',
    ExamType: 'Exam Type',
    ExamDate: 'Exam Date',
    BirthDate: 'Birthdate',
    Phone: 'Phone',
    HMO: 'HMO',
  };

  readonly AImodelNames: {[key: string]: string} = {
    'claude-opus-5': 'Claude Opus 5',
    'claude-sonnet-5': 'Claude Sonnet 5',
    'claude-haiku-4-5': 'Claude Haiku 4.5',
    'gpt-5.5': 'GPT 5.5',
    'gpt-5.4': 'GPT 5.4',
    'gpt-5.4-mini': 'GPT 5.4 mini',
    'gpt-5.4-nano': 'GPT 5.4 nano',
    'gemini-3.5-flash': 'Gemini 3.5 Flash',
    'gemini-3.1-pro-preview': 'Gemini 3.1 Pro Preview',
    'gemini-3-flash-preview': 'Gemini 3 Flash Preview',
    'gemini-3.1-flash-lite': 'Gemini 3.1 Flash-Lite',
  };

  constructor(
    private http: HttpClient,
    private stats: StatsService,
    private sanitizer: DomSanitizer,
    private router: Router,
    private examsService: ExamsService,
    public loginService: LoginService,
    public patientsService: PatientsService,
  ) { }

  ngOnInit(): void {}

  sendAI(response = null, sourceEntry: any = null) {
    this.AIresponse = null; // Reset previous response
    this.AIError = '';
    this.showButton = false;
    const sentQuestion = response || this.userRequest;
    const isFollowup = this.aiHistory.length > 0;
    this.stats.aiQuery(sentQuestion, this.AImodel, isFollowup, this.runGlobally).subscribe((data) => {
      this.AIresponse = data;
      this.showButton = true;
      this.logEntries.push({ timestamp: new Date(), question: sentQuestion, isFollowup, model: this.AImodel, rawResponse: data });
      // Add to history (cast data as any for property access)
      const d: any = data;
      const previewResults = this.normalizeRecordsets(d.results);
      const exportResults = this.normalizeRecordsets(d.exportResults || d.results);
      this.aiHistory.push({
        query: d.query,
        explanation: d.explanation,
        notes: d.notes,
        results: previewResults,
        exportResults,
        userRequest: sentQuestion,
        userResponse: null,
        tempUserResponse: '',
        status: d.status != 'can_prepare_query' ? d.status : null,
        expanded: true,
        dataOutput: d.data_output || 'free_form',
        previewLimit: d.previewLimit || this.getPreviewLimit(d.data_output),
        totalRowCount: d.totalRowCount || this.getTotalRowCount(exportResults),
        displayColumns: Array.isArray(d.displayColumns) ? d.displayColumns : [],
        hiddenColumns: Array.isArray(d.hiddenColumns) ? d.hiddenColumns : [],
        downloadFileName: d.downloadFileName || 'datamine-results.xlsx',
      });
    }, (error) => {
      this.logEntries.push({ timestamp: new Date(), question: sentQuestion, isFollowup, model: this.AImodel, rawResponse: error });
      this.AIError = JSON.stringify(error);
      this.showButton = true;
      if (sourceEntry) {
        sourceEntry.tempUserResponse = sourceEntry.userResponse;
        sourceEntry.userResponse = null;
      }
    });
  }

  saveUserResponse(entry: any) {
    if (entry.tempUserResponse && entry.tempUserResponse.trim()) {
      const response = entry.tempUserResponse.trim();
      entry.userResponse = response;
      entry.tempUserResponse = '';
      this.sendAI(response, entry);
    }
  }

  resetHistory() {
    this.aiHistory = [];
    this.logEntries = [];
  }

  normalizeRecordsets(recordsets: any): any[][] {
    return Array.isArray(recordsets)
      ? recordsets.filter((recordset) => Array.isArray(recordset))
      : [];
  }

  getPreviewLimit(dataOutput: string): number {
    return this.defaultPreviewLimits[dataOutput] || this.defaultPreviewLimits.free_form;
  }

  getTotalRowCount(recordsets: any[][]): number {
    return recordsets.reduce((total, recordset) => total + recordset.length, 0);
  }

  getResultKeys(recordset: any[], entry: DatamineHistoryEntry): string[] {
    if (!recordset || !recordset.length) {
      return [];
    }

    const recordKeys = Object.keys(recordset[0]);
    if (entry.displayColumns && entry.displayColumns.length) {
      return entry.displayColumns.filter((columnName) => recordKeys.includes(columnName));
    }

    const hiddenColumns = new Set(entry.hiddenColumns || []);
    return recordKeys.filter((columnName) => !hiddenColumns.has(columnName));
  }

  getColumnLabel(columnName: string): string {
    return this.columnLabels[columnName] || columnName;
  }

  getShownRowCount(entry: DatamineHistoryEntry): number {
    return this.getTotalRowCount(entry.results);
  }

  hasDownloadData(entry: DatamineHistoryEntry): boolean {
    return this.getTotalRowCount(entry.exportResults) > 0;
  }

  showActionColumn(entry: DatamineHistoryEntry): boolean {
    return entry.dataOutput === 'listing_of_exams' || entry.dataOutput === 'listing_of_patients';
  }

  getActionLabel(entry: DatamineHistoryEntry): string {
    return entry.dataOutput === 'listing_of_exams' ? 'Open readonly' : 'Open history';
  }

  openRow(entry: DatamineHistoryEntry, row: any) {
    if (entry.dataOutput === 'listing_of_exams') {
      this.openExamReadonly(row);
      return;
    }

    if (entry.dataOutput === 'listing_of_patients') {
      this.openPatientHistory(row);
    }
  }

  openExamReadonly(row: any) {
    const pid = Number(row.PID);
    const examId = Number(row.AutoIndex);
    const pregIndex = row.PregIndex !== null && row.PregIndex !== undefined && row.PregIndex !== ''
      ? Number(row.PregIndex)
      : null;

    if (!pid || !examId || !row.ExamType) {
      return;
    }

    this.openInNewTab(['tab', 'exam'], [pid, examId, pregIndex === null ? 'null' : pregIndex], { examType: row.ExamType });
  }

  openPatientHistory(row: any) {
    const pid = Number(row.PID);
    if (!pid) {
      return;
    }

    this.openInNewTab(['tab', 'patient'], [pid]);
  }

  private async openInNewTab(baseSegments: string[], extraSegments: Array<string | number>, queryParams?: {[key: string]: any}) {
    try {
      const response = await this.http.get<{ code: string }>('/api/newExams/resurectionCode').toPromise();
      if (!response || !response.code) {
        return;
      }

      const urlTree = this.router.createUrlTree([...baseSegments, response.code, ...extraSegments], { queryParams });
      const url = this.router.serializeUrl(urlTree);
      window.open(url, '_blank', 'noopener');
    } catch {
      return;
    }
  }

  downloadResults(entry: DatamineHistoryEntry) {
    const workbook = XLSX.utils.book_new();

    this.normalizeRecordsets(entry.exportResults).forEach((recordset, index) => {
      if (!recordset.length) {
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(recordset);
      XLSX.utils.book_append_sheet(workbook, worksheet, `Results ${index + 1}`.slice(0, 31));
    });

    if (!workbook.SheetNames.length) {
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.aoa_to_sheet([['No rows returned']]), 'Results');
    }

    XLSX.writeFile(workbook, entry.downloadFileName || 'datamine-results.xlsx');
  }

  // Sanitize HTML for safe rendering
  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html || '');
  }

  formatCellValue(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'object') {
      return this.prettyJson(value);
    }
    return String(value);
  }

  prettyJson(obj: any): string {
    try {
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }

  prettyJsonWithoutLlmLog(obj: any): string {
    try {
      if (obj && typeof obj === 'object') {
        const { llmLog, ...rest } = obj;
        return JSON.stringify(rest, null, 2);
      }
      return JSON.stringify(obj, null, 2);
    } catch {
      return String(obj);
    }
  }

  prettyJsonOrString(s: string): string {
    if (!s) return '';
    try {
      return JSON.stringify(JSON.parse(s), null, 2);
    } catch {
      return s;
    }
  }
}
